import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import AddonSelector from "@/components/app/AddonSelector";
import MotorcycleSelector from "@/components/app/MotorcycleSelector";
import { api, imageUrl, fmtINR, buildWhatsAppLink } from "@/lib/api";
import { Calendar as CalendarIcon, MapPin, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1600&auto=format&fit=crop";

export default function ListingDetail() {
  const { id } = useParams();

  const [listing, setListing] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [addons, setAddons] = useState({});
  const [moto, setMoto] = useState(null); // { motorcycle, billing_cycle, qty }
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/listings/${id}`).then((r) => setListing(r.data)),
      api.get("/addons").then((r) => setCatalog(r.data)),
    ]).catch(() => toast.error("Could not load listing."));
  }, [id]);

  const images = useMemo(() => {
    if (!listing?.images?.length) return [FALLBACK];
    return listing.images.map((p) => (p.startsWith("http") ? p : imageUrl(p)));
  }, [listing]);

  useEffect(() => {
    if (!listing || !checkIn || !checkOut) { setQuote(null); return; }
    setQuoting(true);
    const payload = {
      listing_id: listing.id,
      guest_name: "guest",
      guest_email: "guest@example.com",
      guest_phone: "0000000000",
      check_in: checkIn,
      check_out: checkOut,
      addons: Object.entries(addons).map(([key, v]) => ({ key, billing_cycle: v.billing_cycle, qty: v.qty })),
    };
    api.post("/bookings/quote", payload).then(({ data }) => setQuote(data)).catch(() => setQuote(null)).finally(() => setQuoting(false));
  }, [listing, checkIn, checkOut, addons]);

  const motoTotal = useMemo(() => {
    if (!moto) return 0;
    const field = { day: "price_per_day", "2_weeks": "price_per_2_weeks", month: "price_per_month" }[moto.billing_cycle];
    return (moto.motorcycle?.[field] || 0) * (moto.qty || 1);
  }, [moto]);

  const grandTotal = (quote?.total || 0) + motoTotal;

  const openWhatsApp = () => {
    if (!listing) return;
    const lines = [
      `Hello HummingStayz! I'd like to book a stay.`,
      ``,
      `*Property:* ${listing.title}`,
      `*Location:* ${listing.area}, ${listing.city}`,
    ];
    if (checkIn && checkOut) {
      lines.push(`*Check-in:* ${checkIn}`);
      lines.push(`*Check-out:* ${checkOut}`);
      if (quote?.nights) lines.push(`*Nights:* ${quote.nights} (${quote.tier})`);
    }
    const selected = Object.entries(addons);
    if (selected.length) {
      lines.push(``, `*Add-ons:*`);
      selected.forEach(([k, v]) => lines.push(`• ${k.replace("_", " ")} × ${v.qty} (${v.billing_cycle.replace("_", " ")})`));
    }
    if (moto) {
      lines.push(``, `*Motorcycle:* ${moto.motorcycle.name}`);
      lines.push(`• Billing: ${moto.billing_cycle.replace("_", " ")} × ${moto.qty}`);
      lines.push(`• Pickup: ${moto.motorcycle.pickup_location}${moto.motorcycle.can_deliver ? " (delivery available)" : ""}`);
      lines.push(`• Insurance / deposit: ${fmtINR(moto.motorcycle.insurance_amount)}`);
      lines.push(`• Bike total: ${fmtINR(motoTotal)}`);
    }
    if (grandTotal) lines.push(``, `*Estimated total:* ${fmtINR(grandTotal)}`);
    window.open(buildWhatsAppLink(lines.join("\n")), "_blank", "noopener");
  };

  if (!listing) {
    return (
      <div className="min-h-screen"><Navbar />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex items-center gap-3 text-sm" data-testid="loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        {/* Header */}
        <div className="mb-8" data-testid="listing-header">
          <div className="overline">Long Stay · Min {listing.min_stay_nights} nights</div>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight">
            {listing.title}
          </h1>
          <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: "var(--text-2)" }}>
            <MapPin className="w-4 h-4" /> {listing.area}, {listing.city}
          </div>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-12">
          <div className="lg:col-span-8 rounded-2xl overflow-hidden h-[280px] sm:h-[420px] lg:h-[520px]">
            <img src={images[activeImg]} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-3">
            {images.slice(0, 4).map((src, i) => (
              <button key={i} onClick={() => setActiveImg(i)}
                className="rounded-2xl overflow-hidden h-[120px] lg:h-[125px]"
                style={{ border: activeImg === i ? "2px solid var(--forest)" : "1px solid var(--line)" }}
                data-testid={`gallery-thumb-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Body grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <section className="surface-card p-8">
              <h3 className="font-serif-display text-2xl mb-3">About this stay</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }} data-testid="listing-description">
                {listing.description}
              </p>
              {listing.amenities?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {listing.amenities.map((a) => (
                    <span key={a} className="px-3 py-1.5 text-xs rounded-full" style={{ background: "var(--surface-alt)" }}>{a}</span>
                  ))}
                </div>
              )}
            </section>

            <section className="surface-card p-8 mt-6">
              <h3 className="font-serif-display text-2xl mb-1">Pricing</h3>
              <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>Best tier is auto-applied based on stay length.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Per night", listing.price_per_night],
                  ["Weekly", listing.price_weekly],
                  ["15 nights", listing.price_15_nights],
                  ["Monthly", listing.price_monthly],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-xl p-4" style={{ background: "var(--surface-alt)" }}>
                    <div className="overline text-[10px]">{l}</div>
                    <div className="mt-1 font-medium text-[color:var(--forest)]">{fmtINR(v)}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="surface-card p-8 mt-6">
              <h3 className="font-serif-display text-2xl mb-1">Add-ons</h3>
              <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>Optional extras curated by the host.</p>
              <AddonSelector catalog={catalog} listingAddons={listing.addons} value={addons} onChange={setAddons} />
            </section>

            <section className="surface-card p-8 mt-6" data-testid="moto-section">
              <h3 className="font-serif-display text-2xl mb-1">Need a motorcycle?</h3>
              <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>
                Browse motorcycles available from our network. Pick one to add it to your booking — the host will confirm pickup/delivery on WhatsApp.
              </p>
              <MotorcycleSelector value={moto} onChange={setMoto} />
            </section>
          </div>

          {/* Booking sidebar */}
          <aside className="lg:col-span-5">
            <div className="surface-card p-7 sticky top-28" data-testid="booking-card">
              <div className="flex items-baseline gap-1">
                <span className="font-serif-display text-3xl text-[color:var(--forest)]">{fmtINR(listing.price_per_night)}</span>
                <span className="text-sm text-[color:var(--text-2)]">/ night</span>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); openWhatsApp(); }} className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline">Check-in</label>
                    <div className="relative mt-1.5">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--text-2)]" />
                      <input type="date" className="input-earthy pl-10"
                        value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="checkin-input" />
                    </div>
                  </div>
                  <div>
                    <label className="overline">Check-out</label>
                    <div className="relative mt-1.5">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--text-2)]" />
                      <input type="date" className="input-earthy pl-10"
                        value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="checkout-input" />
                    </div>
                  </div>
                </div>

                {/* Quote */}
                <div className="rounded-xl p-5 mt-2" style={{ background: "var(--surface-alt)" }} data-testid="quote-block">
                  {quoting ? (
                    <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
                      <Loader2 className="w-4 h-4 animate-spin" /> Calculating…
                    </div>
                  ) : (quote && quote.nights > 0) || moto ? (
                    <div className="space-y-2 text-sm">
                      {quote && quote.nights > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span style={{ color: "var(--text-2)" }}>Stay · {quote.nights} nights ({quote.tier})</span>
                            <span data-testid="quote-stay">{fmtINR(quote.stay_amount)}</span>
                          </div>
                          {quote.lines.map((l) => (
                            <div key={`${l.key}-${l.billing_cycle}`} className="flex justify-between text-xs" style={{ color: "var(--text-2)" }}>
                              <span>{l.key.replace("_", " ")} × {l.qty} ({l.billing_cycle.replace("_", " ")})</span>
                              <span>{fmtINR(l.line_total)}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {moto && (
                        <div className="flex justify-between text-xs" style={{ color: "var(--text-2)" }} data-testid="quote-moto-line">
                          <span>{moto.motorcycle.name} × {moto.qty} ({moto.billing_cycle.replace("_", " ")})</span>
                          <span>{fmtINR(motoTotal)}</span>
                        </div>
                      )}
                      <div className="pt-3 mt-3 border-t flex justify-between font-medium text-base" style={{ borderColor: "var(--line)" }}>
                        <span>Estimated total</span>
                        <span className="text-[color:var(--forest)]" data-testid="quote-total">{fmtINR(grandTotal)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm" style={{ color: "var(--text-2)" }}>Pick dates and add-ons (optional) to preview your total.</div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn-pill w-full"
                  style={{ background: "#25D366", color: "#0B1F12" }}
                  data-testid="whatsapp-book-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Book on WhatsApp
                </button>
                <p className="text-[11px] text-center" style={{ color: "var(--text-3)" }}>
                  You'll be redirected to WhatsApp to confirm the booking with HummingStayz directly.
                </p>
              </form>
            </div>
          </aside>
        </div>
      </main>

      {/* Floating WhatsApp CTA */}
      <a
        href={buildWhatsAppLink(`Hello HummingStayz! I'm interested in *${listing.title}* (${listing.area}, ${listing.city}).`)}
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 btn-pill shadow-2xl"
        style={{ background: "#25D366", color: "#0B1F12" }}
        data-testid="floating-whatsapp"
      >
        <MessageCircle className="w-4 h-4" /> Book on WhatsApp
      </a>

      <Footer />
    </div>
  );
}
